// "Insights IA" (plano Hotel Intelligence, secção 4): regras determinísticas sobre dados já
// calculados no dashboard (nenhuma chamada a IA/API externa — a secção 4 do plano só pede IA
// "quando exista vantagem real", e todos estes insights já são deriváveis dos dados
// existentes). Cada insight deve identificar Tendência, Anomalia, Problema, Oportunidade ou
// Alteração significativa — nunca uma estatística sem valor analítico (ex. "Existiram 174
// conversas este mês", exemplo explicitamente rejeitado no plano).
//
// Cada regra tem um limiar mínimo (contagem, % ou p.p.) para não gerar ruído a partir de
// amostras pequenas — preferível mostrar menos insights a mostrar insights pouco fiáveis.

const TOPIC_MIN_COUNT = 3;
const TOPIC_MIN_CHANGE_PCT = 20;
const HOTEL_RANKING_MIN_CONVERSAS = 3;
const RESOLUTION_MIN_DELTA_PP = 2;
const CSAT_MIN_TOTAL = 5;
const CSAT_NEGATIVE_RATE_THRESHOLD = 20;
const HANDOFF_SHARE_THRESHOLD = 15;
const RESERVAS_MIN_CHANGE_PCT = 20;

function pct(value) {
  return Math.round(value * 10) / 10;
}

function topicInsights(topics, previousTopics, periodDays) {
  const previousByKey = new Map((previousTopics || []).map((t) => [t.key, t.count]));
  const changes = (topics || [])
    .filter((t) => t.key !== 'outros' && t.count >= TOPIC_MIN_COUNT)
    .map((t) => {
      const previous = previousByKey.get(t.key) || 0;
      if (previous === 0) {
        return { label: t.label, kind: 'new', magnitude: t.count };
      }
      const change = ((t.count - previous) / previous) * 100;
      return { label: t.label, kind: 'change', change, magnitude: Math.abs(change) };
    })
    .filter((c) => (c.kind === 'new' ? c.magnitude >= 5 : c.magnitude >= TOPIC_MIN_CHANGE_PCT))
    .sort((a, b) => b.magnitude - a.magnitude)
    .slice(0, 2);

  return changes.map((c) =>
    c.kind === 'new'
      ? {
          text: `Começaram a surgir perguntas sobre ${c.label} nos últimos ${periodDays} dias (${c.magnitude}).`,
          type: 'alteracao',
          magnitude: c.magnitude,
        }
      : {
          text: `As perguntas sobre ${c.label} ${c.change > 0 ? 'aumentaram' : 'diminuíram'} ${pct(Math.abs(c.change))}% nos últimos ${periodDays} dias.`,
          type: 'tendencia',
          magnitude: c.magnitude,
        },
  );
}

function hotelRankingInsight(hotelRanking) {
  if (!hotelRanking || hotelRanking.length < 2) return null;
  const candidate = [...hotelRanking]
    .filter((h) => h.conversas >= HOTEL_RANKING_MIN_CONVERSAS)
    .sort((a, b) => b.taxa_fallback - a.taxa_fallback)[0];
  if (!candidate || candidate.taxa_fallback <= 0) return null;
  return {
    text: `${candidate.hotel_name} tem a maior taxa de pedidos sem resposta do grupo (${pct(candidate.taxa_fallback * 100)}%).`,
    type: 'problema',
    magnitude: candidate.taxa_fallback * 100,
  };
}

function offerInsight(offers) {
  if (!offers?.available || !offers.by_offer?.length) return null;
  const top = offers.by_offer[0]; // já vem ordenado por views desc
  if (!top.views) return null;
  return {
    text: `"${top.offer_title || top.offer_id}" é a oferta com mais interação neste período (${top.views} visualizações).`,
    type: 'oportunidade',
    magnitude: top.views,
  };
}

function resolutionTrendInsight(kpis) {
  const current = kpis?.taxaResolucaoAutomatica;
  const previous = kpis?.taxaResolucaoAutomaticaAnterior;
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }
  const deltaPP = (current - previous) * 100;
  if (Math.abs(deltaPP) < RESOLUTION_MIN_DELTA_PP) return null;
  return {
    text: `A resolução automática ${deltaPP > 0 ? 'melhorou' : 'piorou'} ${pct(Math.abs(deltaPP))} p.p. face ao período anterior.`,
    type: 'tendencia',
    magnitude: Math.abs(deltaPP),
  };
}

function reservasTrendInsight(kpis) {
  const current = kpis?.reservasPeriodo;
  const previous = kpis?.reservasPeriodoAnterior;
  if (!previous || current === null || current === undefined) return null;
  const change = ((current - previous) / previous) * 100;
  if (Math.abs(change) < RESERVAS_MIN_CHANGE_PCT) return null;
  return {
    text: `As reservas iniciadas ${change > 0 ? 'aumentaram' : 'diminuíram'} ${pct(Math.abs(change))}% face ao período anterior.`,
    type: change > 0 ? 'oportunidade' : 'problema',
    magnitude: Math.abs(change),
  };
}

function csatInsight(csat) {
  if (!csat?.available || !csat.total || csat.total < CSAT_MIN_TOTAL) return null;
  if (csat.negative_rate === null || csat.negative_rate < CSAT_NEGATIVE_RATE_THRESHOLD) return null;
  return {
    text: `${pct(csat.negative_rate)}% das avaliações de satisfação neste período são negativas (1–2 estrelas).`,
    type: 'problema',
    magnitude: csat.negative_rate,
  };
}

function handoffInsight(handoff, kpis) {
  if (!handoff?.available || !handoff.total || !kpis?.conversasPeriodo) return null;
  const share = (handoff.total / kpis.conversasPeriodo) * 100;
  if (share < HANDOFF_SHARE_THRESHOLD) return null;
  return {
    text: `${pct(share)}% das conversas no período precisaram de intervenção humana.`,
    type: 'problema',
    magnitude: share,
  };
}

// Máximo de insights simultâneos (secção 4 do plano: "mostrar no máximo 4 a 6 insights
// relevantes"). Ordenados por magnitude (maior efeito primeiro).
const MAX_INSIGHTS = 6;

export function computeInsights({ data, csat, offers, handoff, periodDays }) {
  const candidates = [
    ...topicInsights(data?.intentions, data?.previousIntentions, periodDays),
    hotelRankingInsight(data?.hotelRanking),
    offerInsight(offers),
    resolutionTrendInsight(data?.kpis),
    reservasTrendInsight(data?.kpis),
    csatInsight(csat),
    handoffInsight(handoff, data?.kpis),
  ].filter(Boolean);

  return candidates
    .sort((a, b) => b.magnitude - a.magnitude)
    .slice(0, MAX_INSIGHTS)
    .map((c, i) => ({ id: i, text: c.text, type: c.type }));
}
